/**
 * HomeIQ Design System
 * 
 * Centralized design tokens for consistent UI across the application.
 * All components should reference these constants instead of hardcoding values.
 */

// ─── Typography ───────────────────────────────────────────────────────────────

export const TYPOGRAPHY = {
  // Page hero sections (main pages like Dashboard, Assets, Bookings)
  hero: {
    main: 'text-4xl font-light tracking-tight',
    responsive: 'text-4xl md:text-5xl font-light tracking-tight',
  },
  
  // Detail pages (Asset Detail, Order Detail, Booking Detail)
  hero_detail: 'text-3xl font-light tracking-tight',
  
  // Auth pages and modals
  dialog_title: 'text-2xl font-normal tracking-tight',
  
  // Drawer/Sheet titles
  drawer_title: 'text-xl font-light tracking-tight',
  
  // Section headings
  section: {
    large: 'text-xl font-semibold tracking-tight',
    medium: 'text-lg font-semibold',
    small: 'text-base font-semibold',
  },
  
  // Body text
  body: {
    large: 'text-base',
    default: 'text-sm',
    small: 'text-xs',
  },
  
  // Muted/secondary text
  muted: 'text-sm text-muted-foreground',
} as const;

// ─── Drawer & Sheet Styles ────────────────────────────────────────────────────

export const DRAWER = {
  // Backdrop (overlay behind drawer)
  backdrop: {
    base: 'fixed inset-0 z-40 bg-background/60 backdrop-blur-sm',
    transition: 'transition-opacity duration-300 ease-out',
    open: 'opacity-100 pointer-events-auto',
    closed: 'opacity-0 pointer-events-none',
  },
  
  // Drawer panel
  panel: {
    base: 'fixed right-0 top-14 z-50 h-[calc(100vh-3.5rem)] bg-background border-l shadow-2xl flex flex-col',
    transition: 'transition-transform duration-300 ease-out',
    open: 'translate-x-0',
    closed: 'translate-x-full',
  },
  
  // Width variants
  width: {
    sm: 'max-w-sm w-full',
    md: 'max-w-md w-full',
    lg: 'max-w-lg w-full',
    xl: 'max-w-xl w-full',
    '2xl': 'max-w-2xl w-full',
  },
  
  // Header
  header: {
    container: 'flex items-start justify-between px-6 py-5 border-b shrink-0',
    content: 'space-y-1',
    title: TYPOGRAPHY.drawer_title,
    description: 'text-sm text-muted-foreground',
    closeButton: 'ml-4 shrink-0 p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors',
  },
  
  // Body & Footer
  body: 'flex-1 overflow-y-auto p-6 space-y-6',
  footer: 'px-6 py-4 border-t shrink-0 flex items-center justify-end gap-3',
} as const;

// ─── Animations ───────────────────────────────────────────────────────────────

export const ANIMATIONS = {
  // Fade transitions
  fadeIn: 'animate-fade-in',
  fadeInUp: 'animate-fade-in-up',
  fadeInDown: 'animate-fade-in-down',
  
  // Scale
  scaleIn: 'animate-scale-in',
  
  // Slide
  slideInRight: 'animate-slide-in-right',
  
  // Pulse
  pulseSlow: 'animate-pulse-slow',
  
  // Stagger delays (use with above animations)
  stagger: {
    1: 'stagger-1',
    2: 'stagger-2',
    3: 'stagger-3',
    4: 'stagger-4',
    5: 'stagger-5',
    6: 'stagger-6',
    7: 'stagger-7',
    8: 'stagger-8',
  },
} as const;

// ─── Spacing & Layout ─────────────────────────────────────────────────────────

export const LAYOUT = {
  // Page container
  page: {
    container: 'h-full w-full overflow-y-auto',
    content: 'mx-auto max-w-7xl',
    padding: 'px-4 sm:px-6 lg:px-8',
  },
  
  // Section spacing
  section: {
    gap: 'space-y-6',
    gapLarge: 'space-y-8',
  },
  
  // Card/Panel padding
  card: {
    padding: 'p-6',
    paddingLarge: 'p-8',
  },
} as const;

// ─── Helper Functions ─────────────────────────────────────────────────────────

/**
 * Get hero typography for a page type
 */
export function getHeroClass(type: 'main' | 'detail' | 'auth' | 'responsive' = 'main'): string {
  switch (type) {
    case 'main': return TYPOGRAPHY.hero.main;
    case 'responsive': return TYPOGRAPHY.hero.responsive;
    case 'detail': return TYPOGRAPHY.hero_detail;
    case 'auth': return TYPOGRAPHY.dialog_title;
  }
}

/**
 * Get complete drawer backdrop classes
 */
export function getDrawerBackdropClass(open: boolean): string {
  return `${DRAWER.backdrop.base} ${DRAWER.backdrop.transition} ${
    open ? DRAWER.backdrop.open : DRAWER.backdrop.closed
  }`;
}

/**
 * Get complete drawer panel classes
 */
export function getDrawerPanelClass(open: boolean, width: keyof typeof DRAWER.width = 'md'): string {
  return `${DRAWER.panel.base} ${DRAWER.width[width]} ${DRAWER.panel.transition} ${
    open ? DRAWER.panel.open : DRAWER.panel.closed
  }`;
}
